import { Injectable, computed, inject, signal } from '@angular/core';
import { catchError, forkJoin, of } from 'rxjs';
import {
  BankAccount,
  ContractorDocument,
  ContractorDocumentType,
  StripeConnectedAccount
} from '@umss/shared/data-access';
import { ProfileSetupStepType } from '../models/profile-setup-step-type';
import { PaymentService } from './payment.service';
import { ProfileService } from './profile.service';
import { ProfileStateService } from './profile-state.service';
import { ContractorStripeService } from './stripe.service';

const NON_BG_CHECK_STEP_TYPES = [
  ProfileSetupStepType.BankAccount,
  ProfileSetupStepType.DriversLicense,
  ProfileSetupStepType.ProfessionalLicense,
  ProfileSetupStepType.I9Form,
  ProfileSetupStepType.ImmunizationRecords
] as const;

/**
 * Root-scoped service that computes whether all non-background-check
 * profile steps are complete. Used by components outside the profile
 * setup card (e.g. settings page, background check section).
 */
@Injectable({ providedIn: 'root' })
export class ProfileCompletionService {
  private readonly profileState = inject(ProfileStateService);
  private readonly profileService = inject(ProfileService);
  private readonly paymentService = inject(PaymentService);
  private readonly stripeService = inject(ContractorStripeService);

  private readonly documentsSignal = signal<ContractorDocument[]>([]);
  private readonly bankAccountsSignal = signal<BankAccount[]>([]);
  private readonly stripeAccountSignal = signal<StripeConnectedAccount | null>(
    null
  );
  private readonly loadedSignal = signal(false);

  readonly loaded = this.loadedSignal.asReadonly();

  readonly allOtherStepsComplete = computed(() => {
    if (!this.loadedSignal()) return false;
    const profile = this.profileState.profile();
    const documents = this.documentsSignal();
    const bankAccounts = this.bankAccountsSignal();

    return NON_BG_CHECK_STEP_TYPES.every((type) =>
      this.isStepComplete(type, profile, documents, bankAccounts)
    );
  });

  load(): void {
    if (this.loadedSignal()) return;

    this.profileState.whenReady().subscribe((profile) => {
      forkJoin({
        documents: this.profileService.getDocuments(profile.id, {
          documentTypes: [
            ContractorDocumentType.DRIVERS_LICENSE,
            ContractorDocumentType.IMMUNIZATION_RECORDS
          ]
        }),
        bankAccounts: this.paymentService.getMyBankAccounts(),
        stripeAccount: this.stripeService
          .getAccountStatus()
          .pipe(catchError(() => of(null)))
      }).subscribe({
        next: ({ documents, bankAccounts, stripeAccount }) => {
          this.documentsSignal.set(documents);
          this.bankAccountsSignal.set(bankAccounts);
          this.stripeAccountSignal.set(stripeAccount);
          this.loadedSignal.set(true);
        }
      });
    });
  }

  private isStepComplete(
    type: ProfileSetupStepType,
    profile: ReturnType<typeof this.profileState.profile>,
    documents: ContractorDocument[],
    bankAccounts: BankAccount[]
  ): boolean {
    switch (type) {
      case ProfileSetupStepType.BankAccount:
        return bankAccounts.length > 0;
      case ProfileSetupStepType.DriversLicense:
        return documents.some(
          (d) => d.documentType === ContractorDocumentType.DRIVERS_LICENSE
        );
      case ProfileSetupStepType.ProfessionalLicense:
        return !!profile?.licenseNumber;
      case ProfileSetupStepType.I9Form:
        // W-9 tax info is verified exclusively through Stripe now.
        return this.stripeAccountSignal()?.taxInfoVerified === true;
      case ProfileSetupStepType.ImmunizationRecords:
        return documents.some(
          (d) => d.documentType === ContractorDocumentType.IMMUNIZATION_RECORDS
        );
      default:
        return false;
    }
  }
}
